import { Button, FormControl, InputLabel, MenuItem } from "@mui/material";
import DebouncedTextarea from "components/DebouncedTextarea";
import PSelect from "components/PSelect";
import { FormSection, OutcomeStatus, SectionReviewState, SectionValueView } from "generated/graphql";
import { ReactNode, useState } from "react";
import { stripNull } from "helpers/coll";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useSectionValue } from "./useSectionValue";
import { sortedOutcomeStatuses } from "./useSectionValues";
import { DisplaySectionOutcome } from "./DisplaySectionOutcome";
import { displayOutcomeStatus } from "Backend";
import { useAppSelector } from "redux/hooks";
import { selectLensOutcomeStatusConfiguration } from "redux/slices/clientLens";

const Commentary = ({ textarea, title }: { textarea: ReactNode; title: string }) => {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold">{title}</p>
      <div className="rounded-lg border border-gray-100 p-2">{textarea}</div>
    </div>
  );
};

function AutoCommentary({ text }: { text: string }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-semibold">Auto</p>
      <div className="rounded-lg bg-gray-50 p-2 h-32 overflow-auto">
        <pre style={{ whiteSpace: "pre-wrap" }}>
          <p className="text-xs text-gray-900">{text}</p>
        </pre>
      </div>
    </div>
  );
}

export function SBRSectionStatus({
  caseId,
  formId,
  section,
  sectionValue,
}: {
  caseId: number;
  formId: number;
  section: FormSection;
  sectionValue?: SectionValueView | undefined;
}) {
  const outcomeStatus = sectionValue?.outcomeStatus ?? OutcomeStatus.Unreviewed;
  const { sbrEnabled, lensOutcomeStatusConfigurations } = useAppSelector(selectLensOutcomeStatusConfiguration);
  const outcomeDisplayNames = Object.values(OutcomeStatus).map((status) =>
    displayOutcomeStatus(status, sbrEnabled, lensOutcomeStatusConfigurations)
  );
  const longestOutcomeStatus = outcomeDisplayNames.reduce((longest, current) =>
    current.length > longest.length ? current : longest
  );
  const sectionValueHook = useSectionValue({ caseId, formId });
  const autoComment = stripNull(sectionValue?.outcomeAutoComment);
  const outcomeComment = stripNull(sectionValue?.outcomeComment);
  const qaOutcome = stripNull(sectionValue?.validationComment);
  const showQaComment =
    (qaOutcome !== undefined && qaOutcome !== "") || sectionValue?.reviewState === SectionReviewState.Validation;
  const [expanded, setExpanded] = useState<boolean>(Boolean(showQaComment || autoComment || outcomeComment));

  return (
    <div className="p-4 flex flex-col gap-2 border-b border-gray-100">
      <div className="flex items-center gap-4 mt-4">
        <div
          data-longest-outcome-status={longestOutcomeStatus}
          className="after:content-[attr(data-longest-outcome-status)] after:outcome-status-text after:p-4 after:pr-8 after:block after:invisible after:whitespace-nowrap after:text-xs h-8">
          <FormControl className="w-full">
            <InputLabel>Outcome</InputLabel>
            <PSelect
              label="Outcome"
              value={outcomeStatus}
              slotProps={{
                input: {
                  className: "tracking-wider text-gray-500",
                },
              }}
              onChange={(evt) => sectionValueHook.updateOutcomeStatus(section.id, evt.target.value as OutcomeStatus)}>
              {sortedOutcomeStatuses.map((status) => (
                <MenuItem className="tracking-wider" key={status} value={status}>
                  <DisplaySectionOutcome outcomeStatus={status} />
                </MenuItem>
              ))}
            </PSelect>
          </FormControl>
        </div>
        <div className="grow" />
        <Button
          onClick={() => setExpanded((prev) => !prev)}
          size="small"
          color="default"
          variant="text"
          data-pendo="section-overview-expand"
          endIcon={expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          sx={{ textTransform: "capitalize!important", ml: "6px" }}>
          {expanded ? "Collapse" : "Expand"} Commentary
        </Button>
      </div>
      {expanded && (
        <div className="flex gap-4 mt-2">
          {autoComment !== undefined && (
            <div className="flex-1">
              <AutoCommentary text={autoComment} />
            </div>
          )}
          <div className="flex-1">
            <Commentary
              title="Review"
              textarea={
                <DebouncedTextarea
                  textareaProps={{
                    className: "text-sm",
                  }}
                  minRows={5}
                  maxRows={5}
                  value={outcomeComment ?? ""}
                  onChange={(val) => sectionValueHook.updateOutcomeComment(section.id, val)}
                />
              }
            />
          </div>
          {showQaComment && (
            <div className="flex-1">
              <Commentary
                title="QA"
                textarea={
                  <DebouncedTextarea
                    textareaProps={{ className: "text-sm" }}
                    minRows={5}
                    maxRows={5}
                    value={qaOutcome ?? ""}
                    onChange={(val) => sectionValueHook.updateValidationComment(section.id, val)}
                  />
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
